import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMap } from 'react-leaflet';
import { DARK_BASEMAP_STYLE } from '@/utils/basemap';
import { loadMaplibreLeaflet, supportsWebGL2 } from './maplibreLeaflet';

/** The deepest zoom the maps allow, as when they drew raster tiles. */
export const BASEMAP_MAX_ZOOM = 18;

/**
 * The style to draw: the style JSON as `restyle` recolours it, or the plain
 * style URL when the JSON can't be fetched, so the map still draws.
 */
async function restyledStyle(url, restyle) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return restyle(await res.json());
  } catch (err) {
    console.warn('[DarkBasemap] restyle skipped:', err?.message || err);
    return url;
  }
}

/**
 * The dark basemap (src/utils/basemap.js) under the surrounding react-leaflet
 * map, drawn by MapLibre GL in Leaflet's tile pane, below the pins. It renders
 * nothing itself. The style's credit lands in Leaflet's attribution control.
 * If MapLibre can't load or the device has no WebGL2, the map keeps its pins
 * on its own dark background.
 *
 * `restyle`, when given, recolours the style before it draws: it gets the
 * style's JSON and returns the style to draw (the trip page's flight map,
 * src/utils/flightMapStyle.js). It is read once, when the map mounts, so pass
 * a module-level function.
 */
export default function DarkBasemap({ restyle = null }) {
  const map = useMap();

  useEffect(() => {
    // Without a tile layer Leaflet has no zoom limit, and past the basemap's
    // deepest zoom the pins would drift off it.
    if (map.options.maxZoom === undefined) map.setMaxZoom(BASEMAP_MAX_ZOOM);

    // MapLibre throws on a device without WebGL2, after Leaflet has already
    // wired the layer to every pan and zoom, so don't add it there at all.
    if (!supportsWebGL2()) return undefined;

    let layer = null;
    let cancelled = false;
    loadMaplibreLeaflet()
      .then(async (maplibreGL) => {
        const style = restyle ? await restyledStyle(DARK_BASEMAP_STYLE, restyle) : DARK_BASEMAP_STYLE;
        if (cancelled) return;
        layer = maplibreGL({ style }).addTo(map);
        // OpenFreeMap's dark style names a few icons its sprite sheet lacks
        // ("wood-pattern", "circle-11"). A blank pixel stands in, so each one
        // isn't a console warning on every map.
        const gl = layer.getMaplibreMap();
        gl?.setMissingStyleImageResolver((id) => {
          gl.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) });
        });
      })
      .catch((err) => {
        console.warn('[DarkBasemap] basemap unavailable:', err?.message || err);
      });
    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
    };
  }, [map, restyle]);

  return null;
}

DarkBasemap.propTypes = {
  restyle: PropTypes.func,
};
